import AppLayout from '../../../../../component/appLayout';
import {
  BottomContent,
  Content,
  Data,
  Datatext,
  DataTitle,
  Header,
  HeaderText,
  LeftSide,
  LeftSideData,
  RemoveButton,
  Logo,
  Main,
  RepositoryData,
  Subheader,
  Text,
  Title
} from './styles';
import Button from '../../../../../component/button';
import { useHistory } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { REACT_APP_GITHUB_APP_PUBLIC_URL } from '../../../../../global/environment';
import { deleteGithubAccount, getAccountsList } from '../../../../../services/githubServices';
import Modal from '../../../../../component/modals/modal';
import DeleteModal from '../../../../../component/modals/deleteModal';
import { captureException } from '../../../../../services/logService';
import { BUTTON_TYPE, EMPTY_STATE_SIZE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../../../../global/constants';
import { GitHubUser } from '../../../../../interfaces/SettingsInterface';
import { Group, Container } from '../../../styles';
import IconButton from '../../../../../component/button/iconButton';
import { rootStore } from '../../../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { useMobileDevice } from '../../../../../global/useMobile';
import ResponsiveHeader from '../../../../../component/responsiveHeader';
import SubNavigation from '../../../../../component/bottomNavigation/subNavigation';
import EmptyState from '../../../../../component/emptyState';
import Image from '../../../../../component/image';

function GithubIntegration() {
  //States
  const [selectedModalId, setSelectedModalId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [githubAccountList, setGithubAccountList] = useState<GitHubUser[]>([]);

  // mobx states
  const { workspaceStore } = rootStore;
  const { currentWorkspace } = workspaceStore || {};

  //Other variable
  const history = useHistory();
  const isMobile = useMobileDevice();

  // Loads GitHub account list data for the workspace
  const loadData = useCallback(async () => {
    if (currentWorkspace?.id) {
      const accountList = await getAccountsList();
      setGithubAccountList(accountList);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Opens the delete confirmation modal for a specific account
  const openDeleteModal = useCallback((id: number) => {
    setSelectedModalId(id);
  }, []);

  // Closes the delete confirmation modal
  const onClose = useCallback(() => {
    setSelectedModalId(null);
  }, []);

  // Handles navigation back to previous page
  const handleBackClick = useCallback(() => {
    history.goBack();
  }, [history]);

  // Opens GitHub account linking page in new tab
  const onClickLinkAccount = useCallback(() => {
    const navigateUrl = `${REACT_APP_GITHUB_APP_PUBLIC_URL}/installations/select_target`;
    window.open(navigateUrl, '_blank');
  }, []);

  // Extracts the last segment from a URL
  const getLastSegment = useCallback((url: string): string => {
    const segments = url.split('/');
    return segments.pop() || '';
  }, []);

  // Removes a GitHub account from the workspace
  const removeGithubAccount = useCallback(
    async (accountId: number) => {
      try {
        if (!accountId) return;
        setDeleteLoading(true);
        const response = await deleteGithubAccount(accountId);
        setSelectedModalId(null);
        if (response) {
          loadData();
        }
      } catch (error) {
        captureException(error);
        return;
      } finally {
        setDeleteLoading(false);
      }
    },
    [loadData]
  );

  return (
    <>
      <AppLayout>
        <Container className='desktop-content'>
          <Group>
            {!isMobile ? (
              <Main>
                <Header>
                  <IconButton
                    iconName='new-large-back-arrow-icon'
                    size={ICON_BUTTON_SIZE.SMALL}
                    variant={ICON_BUTTON_TYPE.TERTIARY}
                    onClick={handleBackClick}
                  />
                  <Title>Integrations</Title>
                </Header>
              </Main>
            ) : (
              <ResponsiveHeader
                subTitle='Integrations'
                image={{
                  url: 'github_logo',
                  title: 'github-logo'
                }}
                title='Github'
              />
            )}
            <BottomContent>
              <Subheader>
                <LeftSide>
                  {!isMobile && (
                    <Logo>
                      <Image imageKey='github_logo' alt='github-logo' width={42} height={42} />
                    </Logo>
                  )}
                  <Content>
                    {!isMobile && <Text>Github</Text>}
                    {githubAccountList?.length > 0 && (
                      <HeaderText>Automate pull request and commit workflows with task sync.</HeaderText>
                    )}
                  </Content>
                </LeftSide>
                {githubAccountList?.length > 0 && (
                  <Button title='Link Github Account' variant={BUTTON_TYPE.PRIMARY} onClick={onClickLinkAccount} />
                )}
              </Subheader>
              {githubAccountList?.length === 0 ? (
                <EmptyState
                  imageUrl='project_empty'
                  size={EMPTY_STATE_SIZE.MEDIUM}
                  title='No Github accounts linked'
                  description='Connect your Github account to sync pull requests and commits with tasks.'
                  isAlignedCenter={true}
                  buttonTitle='Link Github Account'
                  onClick={onClickLinkAccount}
                />
              ) : (
                <RepositoryData>
                  {githubAccountList?.map((item) => {
                    const dataText = getLastSegment(item?.accountUrl);
                    return (
                      <Data key={item?.id}>
                        <LeftSideData>
                          <DataTitle>{item?.account}</DataTitle>
                          <Datatext>{`/${dataText} - ${item?.totalRepositories} Repository`} </Datatext>
                        </LeftSideData>
                        <RemoveButton role='button' onClick={() => openDeleteModal(item?.installationId)}>
                          Remove
                        </RemoveButton>
                      </Data>
                    );
                  })}
                </RepositoryData>
              )}
            </BottomContent>
          </Group>
        </Container>
      </AppLayout>
      <SubNavigation />
      <Modal open={selectedModalId !== null} onClose={onClose} maxWidth={'334px'}>
        <DeleteModal
          onClose={onClose}
          onOk={() => removeGithubAccount(selectedModalId as number)}
          loading={deleteLoading}
          modalTitle=' Remove Github Account'
          description=' Are you sure you want to remove this account?'
        />
      </Modal>
    </>
  );
}

export default observer(GithubIntegration);
